import { useEffect, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

/**
 * Hook that automatically refreshes the user's session when they are active.
 * This prevents session expiration for active users.
 */
export function useSessionRefresh() {
  const { session } = useAuth();
  const lastActivityRef = useRef(Date.now());
  const refreshingRef = useRef(false);

  useEffect(() => {
    if (!session) return;

    // Check if session is close to expiring and refresh if needed
    const checkAndRefresh = async () => {
      const expiresAt = session.expires_at;
      if (!expiresAt || refreshingRef.current) return;

      const expiresIn = expiresAt - Math.floor(Date.now() / 1000);

      // Refresh if less than 5 minutes remaining
      if (expiresIn < 300) {
        refreshingRef.current = true;
        try {
          await supabase.auth.refreshSession();
        } catch (error) {
          console.error('Error refreshing session:', error);
        } finally {
          refreshingRef.current = false;
        }
      }
    };

    // Check every 60 seconds
    const interval = setInterval(checkAndRefresh, 60 * 1000);

    // Also refresh on user activity after 10 minutes of inactivity
    const handleActivity = () => {
      const now = Date.now();
      const timeSinceLastActivity = now - lastActivityRef.current;

      // If more than 10 minutes since last activity, refresh session
      if (timeSinceLastActivity > 10 * 60 * 1000) {
        checkAndRefresh();
      }
      lastActivityRef.current = now;
    };

    const events = ['mousedown', 'keydown', 'scroll', 'touchstart'];
    events.forEach((e) => window.addEventListener(e, handleActivity, { passive: true }));

    // Initial check
    checkAndRefresh();

    return () => {
      clearInterval(interval);
      events.forEach((e) => window.removeEventListener(e, handleActivity));
    };
  }, [session]);
}
